import React, { useEffect, useState } from "react"
import { CountriesData } from "../CountriesData"
import { ISession, ISessionGetFilter, REQ_SESSION_GET, S_SESSION_LOADED } from "../managers/Sessions"
import dayjs from "dayjs"
import { Price } from "../components/Price"
import { Box } from "../components/box/Box"
import { Typo } from "../components/text/Typo"
import { useTheme } from "styled-components"
import { Panel } from "../components/Panel"
import { Spacer } from "../components/Spacer"

// ─── Constants ──────────────────────────────────────────────────────────────

const statColor: Record<string, string> = {
    completed:  "#06ef96",
    fail:       "#e91e63",
    error:      "#b72556",
    canceled:   "#795548",
    processing: "#2196f3",
    problem:    "#9c27b0",
    abandoned:  "#ff9800",
    processed:  "#21f3db",
}

const PAYMENT_SYSTEMS = ["ap", "gp", "ob", "yapily", "yapily_predefined", "yapily_repay"]
const PS_LABELS: Record<string, string> = {
    ap:                "AP",
    gp:                "GP",
    ob:                "OB",
    yapily:            "Yapily",
    yapily_predefined: "Predefined",
    yapily_repay:      "Repay",
}
const PS_EXPAND: Record<string, string[]> = { ob: ["yapily", "yapily_predefined"] }

const ALL_STATUSES = ["completed", "processed", "processing", "fail", "error", "problem", "abandoned", "canceled", "expired"]


function todayStr() { return dayjs().format("YYYY-MM-DD") }

// ─── Chip ───────────────────────────────────────────────────────────────────

const Chip = ({ label, active, dim, onClick }: { label: string; active: boolean; dim: boolean; onClick: () => void }) => (
    <Box onClick={onClick} sx={{
        padding: "3px 10px", borderRadius: "20px", cursor: "pointer", userSelect: "none", flexShrink: 0,
        border: active ? "1px solid rgba(60,180,255,.85)" : "1px solid rgba(255,255,255,.18)",
        backgroundColor: active ? "rgba(60,180,255,.22)" : "rgba(255,255,255,.03)",
        opacity: dim ? 0.35 : 1,
        transition: "opacity .15s",
    }}>
        <Typo variant="small" sx={{ fontSize: "11px" }} color={active ? "rgb(120,210,255)" : "rgba(255,255,255,.6)"}>
            {label}
        </Typo>
    </Box>
)

// ─── PageBtn ─────────────────────────────────────────────────────────────────

const PageBtn = ({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: React.ReactNode }) => (
    <Box onClick={disabled ? undefined : onClick} sx={{
        padding: "2px 8px", borderRadius: "4px", cursor: disabled ? "default" : "pointer",
        border: "1px solid rgba(255,255,255,.1)",
        backgroundColor: "rgba(255,255,255,.04)",
        opacity: disabled ? 0.3 : 1,
        transition: "opacity .15s",
        userSelect: "none",
        "&:hover": disabled ? {} : { backgroundColor: "rgba(255,255,255,.08)" },
    }}>
        <Typo variant="small" sx={{ fontSize: "11px" }} color="rgba(255,255,255,.5)">{children}</Typo>
    </Box>
)

// ─── ChipGroup ──────────────────────────────────────────────────────────────

const ChipGroup = ({
    label, options, labelMap, selected, onChange, radio = false,
}: {
    label: string
    options: string[]
    labelMap?: Record<string, string>
    selected: string[]
    onChange: (v: string[]) => void
    radio?: boolean
}) => {
    const toggle = (opt: string) => {
        if (radio)
            onChange(selected.includes(opt) ? [] : [opt])
        else
            onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt])
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "10px", letterSpacing: "0.08em" }}>
                {label.toUpperCase()}
            </Typo>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                {selected.length > 0 && (
                    <Chip label="ALL" active={false} dim={false} onClick={() => onChange([])} />
                )}
                {options.map(opt => {
                    const active = selected.includes(opt)
                    const dim = selected.length > 0 && !active
                    return (
                        <Chip key={opt} label={labelMap?.[opt] ?? opt.toUpperCase()} active={active} dim={dim} onClick={() => toggle(opt)} />
                    )
                })}
            </Box>
        </Box>
    )
}

// ─── DateInput ──────────────────────────────────────────────────────────────

const DateInput = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "10px", letterSpacing: "0.08em" }}>
            {label}
        </Typo>
        <input
            type="date" value={value}
            onChange={e => onChange(e.target.value)}
            style={{
                background: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.15)",
                color: "#fff", borderRadius: 4, padding: "5px 8px", fontSize: 12,
                colorScheme: "dark", height: "28px", boxSizing: "border-box",
            }}
        />
    </Box>
)

// ─── CountryPicker ───────────────────────────────────────────────────────────

const FLAG = ({ code }: { code: string }) => (
    <Box sx={{
        backgroundSize: "cover", backgroundPosition: "center",
        width: "20px", height: "14px", borderRadius: "2px", flexShrink: 0,
        backgroundColor: "rgba(255,255,255,.1)",
    }} style={{ backgroundImage: `url(https://flagcdn.com/${code.toLowerCase()}.svg)` }} />
)

const CountryPicker = ({ value, onChange, onClose }: {
    value: string
    onChange: (v: string) => void
    onClose: () => void
}) => {
    const theme = useTheme()
    const [search, setSearch] = useState("")
    const inputRef = React.useRef<HTMLInputElement>(null)

    useEffect(() => { inputRef.current?.focus() }, [])

    const all = CountriesData.getNormalizedCountries("en")
    const q = search.toLowerCase()
    const filtered = q
        ? all.filter(c => c.name.toLowerCase().includes(q) || c.alpha2.toLowerCase().startsWith(q))
        : all

    const select = (alpha2: string) => {
        onChange(alpha2.toLowerCase())
        onClose()
    }

    return (
        <Box onClick={onClose} sx={{
            position: "fixed", inset: 0, zIndex: 200,
            backgroundColor: "rgba(0,0,0,.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Box onClick={(e: any) => e.stopPropagation()} sx={{
                width: "340px", maxHeight: "520px",
                backgroundColor: theme.colors.panelBgColor,
                borderRadius: "6px",
                boxShadow: "0 0 40px rgba(0,0,0,.9)",
                border: "1px solid rgba(255,255,255,.08)",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
                animation: "slideUp .18s ease-out",
            }}>
                {/* header */}
                <Box sx={{
                    padding: "12px 14px 10px",
                    borderBottom: "1px solid rgba(255,255,255,.07)",
                    display: "flex", alignItems: "center", gap: "10px",
                }}>
                    <Typo variant="small" sx={{ fontSize: "11px", letterSpacing: "0.1em", flexGrow: 1 }}
                        color="rgba(255,255,255,.4)">SELECT COUNTRY</Typo>
                    {value && (
                        <Box onClick={() => { onChange(""); onClose() }} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                            <FLAG code={value} />
                            <Typo variant="small" sx={{ fontSize: "11px" }} color="rgba(60,180,255,.9)">{value.toUpperCase()}</Typo>
                        </Box>
                    )}
                    <PageBtn disabled={false} onClick={onClose}>✕</PageBtn>
                </Box>

                {/* search */}
                <Box sx={{ padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
                    <input
                        ref={inputRef}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search..."
                        style={{
                            width: "100%", boxSizing: "border-box",
                            background: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.12)",
                            color: "#fff", borderRadius: 4, padding: "5px 10px", fontSize: 12,
                            colorScheme: "dark", outline: "none",
                        }}
                    />
                </Box>

                {/* list */}
                <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
                    {filtered.map(c => {
                        const active = value === c.alpha2.toLowerCase()
                        return (
                            <Box key={c.alpha2} onClick={() => select(c.alpha2)} sx={{
                                display: "flex", alignItems: "center", gap: "10px",
                                padding: "7px 14px", cursor: "pointer",
                                backgroundColor: active ? "rgba(60,180,255,.12)" : "transparent",
                                borderBottom: "1px solid rgba(255,255,255,.03)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,.05)" },
                                transition: "background-color .1s",
                            }}>
                                <FLAG code={c.alpha2} />
                                <Typo variant="small" sx={{ fontSize: "12px" }}
                                    color={active ? "rgb(120,210,255)" : "rgba(255,255,255,.75)"}>
                                    {c.name}
                                </Typo>
                                <Spacer grow />
                                <Typo variant="small" sx={{ fontSize: "10px" }} color="rgba(255,255,255,.2)">
                                    {c.alpha2}
                                </Typo>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}

// ─── CountryInput (trigger) ──────────────────────────────────────────────────

const CountryInput = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
    const [open, setOpen] = useState(false)
    const country = value ? CountriesData.getCountryByAlpha2(value) : null

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "10px", letterSpacing: "0.08em" }}>COUNTRY</Typo>
            <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <Box onClick={() => setOpen(true)} sx={{
                    display: "flex", alignItems: "center", gap: "7px",
                    height: "28px", padding: "0 10px", boxSizing: "border-box",
                    background: "rgba(0,0,0,.35)",
                    border: value ? "1px solid rgba(60,180,255,.45)" : "1px solid rgba(255,255,255,.15)",
                    borderRadius: "4px", cursor: "pointer", minWidth: "120px",
                    "&:hover": { borderColor: "rgba(255,255,255,.3)" },
                    transition: "border-color .15s",
                }}>
                    {value
                        ? <><FLAG code={value} />
                            <Typo variant="small" sx={{ fontSize: "12px" }} color="rgba(255,255,255,.85)">
                                {country?.name ?? value.toUpperCase()}
                            </Typo></>
                        : <Typo variant="small" sx={{ fontSize: "12px" }} color="rgba(255,255,255,.25)">
                            Select country...
                        </Typo>
                    }
                </Box>
                {value && (
                    <PageBtn disabled={false} onClick={() => onChange("")}>×</PageBtn>
                )}
            </Box>
            {open && (
                <CountryPicker value={value} onChange={onChange} onClose={() => setOpen(false)} />
            )}
        </Box>
    )
}

// ─── SessionsPanel ──────────────────────────────────────────────────────────

export const SessionsPanel = ({ initialFilter }: { initialFilter?: ISessionGetFilter }) => {
    const theme = useTheme()

    const [from,         setFrom]         = useState(initialFilter?.from ?? todayStr())
    const [to,           setTo]           = useState(initialFilter?.to   ?? todayStr())
    const [searchInput,  setSearchInput]  = useState(initialFilter?.search ?? "")
    const [activeSearch, setActiveSearch] = useState(initialFilter?.search ?? "")
    const [selectedPS,   setSelectedPS]   = useState<string>(initialFilter?.payment_system?.[0] ?? "")
    const [selStatus,    setSelStatus]    = useState<string[]>(initialFilter?.status ?? [])
    const [country,          setCountry]          = useState(initialFilter?.country ?? "")
    const [instInput,        setInstInput]        = useState(initialFilter?.institution ?? "")
    const [activeInstitution,setActiveInstitution]= useState(initialFilter?.institution ?? "")
    const [merchantIds] = useState<number[]>(initialFilter?.merchant_id ?? [])
    const [page,         setPage]         = useState(0)
    const LIMIT = 50

    const [result, sendRequest, , busy] = REQ_SESSION_GET.useRequest()

    const buildFilter = (p: number): ISessionGetFilter => ({
        from, to, page: p, limit: LIMIT,
        ...(activeSearch                && { search:         activeSearch }),
        ...(selectedPS                  && { payment_system: PS_EXPAND[selectedPS] ?? [selectedPS] }),
        ...(selStatus.length > 0        && { status:         selStatus }),
        ...(country.length === 2        && { country }),
        ...(activeInstitution           && { institution: activeInstitution }),
        ...(merchantIds.length > 0      && { merchant_id:    merchantIds }),
    })

    const load = (p: number) => {
        sendRequest(buildFilter(p))
        setPage(p)
    }

    const commitSearch = () => {
        setActiveSearch(searchInput)
    }

    const resetSearch = () => {
        setSearchInput("")
        setActiveSearch("")
    }

    // auto-load when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(0) }, [from, to, selectedPS, selStatus, country, merchantIds])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (activeSearch !== undefined) load(0) }, [activeSearch])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(0) }, [activeInstitution])

    const rows   = result?.rows   ?? []
    const count  = result?.count  ?? 0
    const pages  = result?.pages  ?? 0

    return (
        <Box sx={{ padding: theme.value(5), height: "100%" }}>
            <Panel busy={busy} label="Sessions" sx={{ height: "100%" }}>

                {/* ── Filters ─────────────────────────────────────────── */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: theme.value(4) }}>

                    {/* Row 1: dates + search */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: theme.value(4), alignItems: "flex-end" }}>
                        <DateInput label="FROM" value={from} onChange={setFrom} />
                        <DateInput label="TO"   value={to}   onChange={setTo}   />
                        <CountryInput value={country} onChange={setCountry} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "10px", letterSpacing: "0.08em" }}>INSTITUTION</Typo>
                            <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                <input
                                    value={instInput}
                                    onChange={e => setInstInput(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && setActiveInstitution(instInput.trim())}
                                    onBlur={() => setActiveInstitution(instInput.trim())}
                                    placeholder="bank name..."
                                    style={{
                                        background: "rgba(0,0,0,.35)", border: activeInstitution ? "1px solid rgba(60,180,255,.45)" : "1px solid rgba(255,255,255,.15)",
                                        color: "#fff", borderRadius: 4, padding: "5px 8px", fontSize: 12,
                                        colorScheme: "dark", height: "28px", boxSizing: "border-box", width: "140px",
                                    }}
                                />
                                {activeInstitution && (
                                    <PageBtn disabled={false} onClick={() => { setInstInput(""); setActiveInstitution("") }}>×</PageBtn>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flexGrow: 1, minWidth: "180px" }}>
                            <Typo variant="small" color="rgba(255,255,255,.4)" sx={{ fontSize: "10px", letterSpacing: "0.08em" }}>SEARCH</Typo>
                            <Box sx={{ display: "flex", gap: "6px" }}>
                                <input
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && commitSearch()}
                                    placeholder="SSID / trx id / email..."
                                    style={{
                                        background: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.15)",
                                        color: "#fff", borderRadius: 4, padding: "5px 8px", fontSize: 12,
                                        flex: 1, colorScheme: "dark", height: "28px", boxSizing: "border-box",
                                    }}
                                />
                                <PageBtn disabled={busy} onClick={commitSearch}>GO</PageBtn>
                                {activeSearch && <PageBtn disabled={false} onClick={resetSearch}>×</PageBtn>}
                            </Box>
                        </Box>
                    </Box>

                    {/* Row 2: PS + status */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: theme.value(6), alignItems: "flex-start" }}>
                        <ChipGroup
                            label="Payment System"
                            options={PAYMENT_SYSTEMS}
                            labelMap={PS_LABELS}
                            selected={selectedPS ? [selectedPS] : []}
                            onChange={v => setSelectedPS(v[0] ?? "")}
                            radio
                        />
                        <ChipGroup
                            label="Status"
                            options={ALL_STATUSES}
                            selected={selStatus}
                            onChange={setSelStatus}
                        />
                    </Box>

                </Box>

                <Spacer size={theme.value(6)} />

                {/* ── Table ───────────────────────────────────────────── */}
                <Box sx={{ flexGrow: "1", overflow: "auto", position: "relative" }}>
                    <Box sx={{ position: "absolute", width: "100%" }}>
                        {rows.length > 0
                            ? <SessionTable sessions={rows} />
                            : !busy && (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                                    <Typo color="rgba(255,255,255,.25)">NO DATA</Typo>
                                </Box>
                            )
                        }
                    </Box>
                </Box>

                <Spacer size={theme.value(4)} />

                {/* ── Footer ──────────────────────────────────────────── */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typo variant="small" color="rgba(255,255,255,.25)" sx={{ fontSize: "11px" }}>
                        {count > 0 ? `${count.toLocaleString()} sessions` : ""}
                    </Typo>
                    <Spacer grow />
                    {pages > 1 && <>
                        <PageBtn disabled={busy || page === 0}        onClick={() => load(page - 1)}>◀</PageBtn>
                        <Typo variant="small" sx={{ fontSize: "11px" }} color="rgba(255,255,255,.4)">{page + 1} / {pages}</Typo>
                        <PageBtn disabled={busy || page >= pages - 1} onClick={() => load(page + 1)}>▶</PageBtn>
                    </>}
                    <PageBtn disabled={busy} onClick={() => load(page)}>{busy ? "..." : "↻"}</PageBtn>
                </Box>

            </Panel>
        </Box>
    )
}

// ─── Selected Session (legacy — used by SearchSessionForm) ──────────────────

export const SelectedSession = ({ session }: { session?: ISession }) => {
    if (!session) return <div>PROBLEM!</div>
    return (
        <Box sx={{ position: "sticky", top: "0", display: "block", whiteSpace: "pre", overflow: "auto" }}>
            {JSON.stringify(session, null, "\t")}
        </Box>
    )
}

// ─── Session Table ───────────────────────────────────────────────────────────

const SessionTable = ({ sessions }: { sessions: ISession[] }) => {
    const theme = useTheme()

    return (
        <Box sx={{
            display: "grid",
            position: "relative",
            fontSize: "0.9em",
            gridTemplateColumns: "max-content max-content minmax(50px,.6fr) minmax(50px,.6fr) minmax(50px,auto) minmax(60px,1fr) minmax(80px,160px) max-content",
            [`@media (max-width: ${theme.values.rebuildWidth})`]: {
                gridTemplateColumns: "0 max-content 0 minmax(50px,.6fr) minmax(50px,auto) minmax(40px,1fr) minmax(60px,120px) max-content",
                fontSize: ".9em",
            },
        }}>
            {/* Header */}
            <Box sx={{
                display: "contents",
                "&>div": {
                    padding: theme.values.padding,
                    borderBottom: "1px solid rgba(0,0,0,.5)",
                    display: "flex", flexDirection: "column", justifyContent: "center",
                    textTransform: "uppercase", zIndex: 1,
                    backgroundColor: "rgba(0,0,0,.4)",
                    fontSize: "12px", fontWeight: "600",
                    position: "sticky", top: "0", overflow: "hidden",
                },
                "&>div:nth-child(1)": { [`@media (max-width: ${theme.values.rebuildWidth})`]: { visibility: "hidden" } },
                "&>div:nth-child(3)": { [`@media (max-width: ${theme.values.rebuildWidth})`]: { visibility: "hidden" } },
            }}>
                <div>Country</div>
                <div>Trx ID</div>
                <div>Terminal</div>
                <div>User</div>
                <div>Source</div>
                <div>Fail reason</div>
                <div>Amount</div>
                <div>Status</div>
            </Box>

            {/* Rows */}
            {sessions.map(val => {
                const isTest = val.transaction_id.indexOf("111110") !== -1 || val.terminal === "test_psys"
                return (
                    <Box key={val.ssid} sx={{
                        display: "contents",
                        "&>div": {
                            padding: theme.values.padding,
                            borderBottom: "1px solid rgba(0,0,0,.3)",
                            borderTop: "1px solid rgba(255,255,255,.07)",
                            cursor: "pointer",
                            display: "flex", gap: "5px", flexDirection: "column", justifyContent: "center",
                            position: "relative", transition: "background-color .2s",
                            whiteSpace: "nowrap", overflow: "hidden", minWidth: 0,
                            opacity: isTest ? 0.5 : 1,
                        },
                        "&>div:first-child": { borderLeft: "4px solid " + (statColor[val.status] ?? "#555") },
                        "&>div:nth-child(1)": { [`@media (max-width: ${theme.values.rebuildWidth})`]: { visibility: "hidden" } },
                        "&>div:nth-child(3)": { [`@media (max-width: ${theme.values.rebuildWidth})`]: { visibility: "hidden" } },
                        "&:hover>div": { backgroundColor: "rgba(0,0,0,.3)" },
                    }} onClick={() => S_SESSION_LOADED.invoke(val)}>

                        <div title={val.country?.toUpperCase()}>
                            <Box sx={{
                                backgroundSize: "cover", backgroundPosition: "center",
                                minWidth: "20px", minHeight: "20px", maxWidth: "20px", maxHeight: "20px",
                                backgroundColor: "rgba(0,0,0,.3)", borderRadius: "40%", alignSelf: "center",
                            }} style={{ backgroundImage: val.country ? `url(https://flagcdn.com/${val.country}.svg)` : undefined }} />
                        </div>

                        <div>
                            <Typo variant="small">{val.ctime?.toUpperCase()}</Typo>
                            <Typo variant="caption">{val.transaction_id}</Typo>
                        </div>

                        <div>
                            <Typo variant="small">{val.branch?.toUpperCase()}</Typo>
                            <Typo variant="caption">{val.terminal}</Typo>
                        </div>

                        <div>
                            <Typo variant="small">{val.ip ?? "??.??.??.??"}</Typo>
                            <Typo variant="caption">{val.email}</Typo>
                        </div>

                        <div>
                            <Typo variant="small">{val.paymentSystem}{val.institution ? " — " + val.institution : ""}</Typo>
                            <Typo variant="caption">{val.card_mask ?? "..."}</Typo>
                        </div>

                        <div style={{ whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word", overflow: "hidden", minWidth: 0 }}>
                            {val.fail_reason && (
                                <Typo variant="small" sx={{ fontSize: "11px", color: "#ff5c8a", lineHeight: "1.3" }}>
                                    {val.fail_reason}
                                </Typo>
                            )}
                        </div>

                        <Box sx={{ backgroundColor: "rgba(0,0,0,.1)" }}>
                            <Price amount={val.amount} currency={val.currency} />
                        </Box>

                        <Box sx={{ backgroundColor: "rgba(0,0,0,.2)" }}>
                            <Typo variant="small" sx={{ textTransform: "uppercase", fontWeight: "bold", color: statColor[val.status] ?? "#aaa" }}>
                                {val.status}
                            </Typo>
                            <Typo variant="small" sx={{ fontSize: "10px", textTransform: "uppercase" }}>
                                {val.gate_notify}
                            </Typo>
                        </Box>

                    </Box>
                )
            })}
        </Box>
    )
}
